import { Capacitor } from '@capacitor/core'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'

// OS-backed secure storage (Android Keystore / iOS Keychain) for the persistent
// native session. No-op on web — web auth lives entirely in the HttpOnly cookie.
//
// The plugin is imported unconditionally but every method is guarded by
// `Capacitor.isNativePlatform()`, so the web bundle never calls the (throwing)
// web stub. See the auth persistence plan, Pitfall 10.

export const AUTH_JWT_KEY = 'auth_jwt'
export const AUTH_REFRESH_KEY = 'auth_refresh'

const isNative = () => Capacitor.isNativePlatform()

export const secureStore = {
  async set(key: string, value: string): Promise<void> {
    if (!isNative()) return
    await SecureStoragePlugin.set({ key, value })
  },

  // `SecureStoragePlugin.get` REJECTS when the key is missing (unlike
  // Preferences) — and can also throw if the entry became undecryptable after a
  // Keystore invalidation (lock-screen/biometric change). Either way: purge the
  // entry so it can't perpetually throw, and treat the user as logged out.
  async get(key: string): Promise<string | null> {
    if (!isNative()) return null
    try {
      const { value } = await SecureStoragePlugin.get({ key })
      return value
    } catch {
      await this.remove(key)
      return null
    }
  },

  async remove(key: string): Promise<void> {
    if (!isNative()) return
    try {
      await SecureStoragePlugin.remove({ key })
    } catch {
      // Already absent — nothing to do.
    }
  },

  // Wipes both auth keys. Used by the fresh-install reset (iOS Keychain survives
  // app uninstall) and on logout.
  async clearAll(): Promise<void> {
    if (!isNative()) return
    await this.remove(AUTH_JWT_KEY)
    await this.remove(AUTH_REFRESH_KEY)
  },
}
